import { Fragment } from 'react';
import { Card } from '@safetyhub/ui';
import { formatFlowTime } from './approvalFlow';
import type { ApprovalFlowNode, ApprovalFlowNodeStatus } from './approvalFlow';
import { AlertTriangleIcon, CheckIcon, CircleIcon, StepClockIcon, XIcon } from '../../components/icons';

/**
 * 결재 진행 스텝퍼 — 목업 `.appr-flow` 기반, 분배 규칙만 사용자 확정(2026-08-02)으로 교체.
 *
 * **단계 블록도 커넥터도 상한이 있는 고정 피치** — 단계 수와 무관하게 같은 번째 단계가
 * 모든 카드에서 같은 x에 선다(`1-2-3`, `1-2`, `1-2-3-4` 전부 왼쪽부터 같은 간격). 두 번
 * 갈아엎은 결과다: 단계 flex:1(목업)은 단계 수에 따라 위치가 흔들리고, 커넥터 flex:1
 * (양끝 고정)은 넓은 화면에서 2단계 카드의 선이 1,300px씩 그어져 과했다. 커넥터는
 * flex-1에 max-w 상한 — 넓으면 상한에 걸려 피치가 고정되고, 좁으면 min까지 줄어든다.
 *
 * 단계 수가 가변(결재란은 지정하기 나름)이라 노드가 늘어나면 커넥터가 최소폭까지 줄고
 * 가로 스크롤로 흘린다. 줄바꿈(wrap)을 쓰지 않는 이유는 커넥터 바가 행 끝에서 허공에
 * 걸려 진행 방향이 끊겨 보이기 때문.
 */

// `parked`는 **`current`와 다른 색**을 쓴다(S39). 둘 다 "아직 처리 안 됨"이지만 amber로 뭉치면
// *"곧 누가 결재할 것"* 으로 읽혀 **멈춰 있다는 사실이 목록에서 사라진다** — 그게 S73의 원래
// 증상이었다. 조치가 필요한 상태이므로 반려(rose)와 진행 중(amber) 사이의 경고 톤으로 둔다.
const NODE_CLASS: Record<ApprovalFlowNodeStatus, string> = {
  done: 'border-emerald-600 bg-emerald-50 text-emerald-600',
  current: 'border-amber-600 bg-amber-50 text-amber-600',
  parked: 'border-orange-600 bg-orange-50 text-orange-600',
  rejected: 'border-rose-600 bg-rose-50 text-rose-600',
  wait: 'border-dashed border-ink-300 bg-white text-ink-400',
};

const LABEL_CLASS: Record<ApprovalFlowNodeStatus, string> = {
  done: 'text-emerald-600',
  current: 'text-amber-600',
  parked: 'text-orange-600',
  rejected: 'text-rose-600',
  wait: 'text-ink-400',
};

const NAME_CLASS: Record<ApprovalFlowNodeStatus, string> = {
  done: 'text-ink-700',
  current: 'text-ink-700',
  parked: 'text-orange-700',
  rejected: 'text-ink-700',
  wait: 'text-ink-400',
};

/** 색만으로 구분되지 않도록 보조 텍스트로도 상태를 읽히게 한다. */
const STATUS_TEXT: Record<ApprovalFlowNodeStatus, string> = {
  done: '완료',
  current: '진행 중',
  // "대기"(wait=아직 차례가 안 옴)와 **다른 말이어야 한다** — 이 칸은 기다려도 아무 일도
  // 일어나지 않고, 사람이 담당자를 정해야 움직인다. 무엇을 해야 하는지를 그대로 적는다.
  parked: '담당자 지정 필요',
  rejected: '반려',
  wait: '대기',
};

function StepIcon({ status }: { status: ApprovalFlowNodeStatus }) {
  const className = 'h-[13px] w-[13px]';
  if (status === 'done') return <CheckIcon className={className} />;
  if (status === 'rejected') return <XIcon className={className} />;
  // 시계(=기다리는 중)가 아니라 경고다 — 이 칸은 기다림으로 풀리지 않는다.
  if (status === 'parked') return <AlertTriangleIcon className={className} />;
  if (status === 'current') return <StepClockIcon className={className} />;
  return <CircleIcon className={className} />;
}

/** 노드 시각 표기 — 완료/반려는 처리 시각, 진행 중은 "대기 중", 미도달은 "-". */
function stepTimeText(node: ApprovalFlowNode): string {
  if (node.at) return formatFlowTime(node.at);
  // ⚠️ 대기 칸에 "대기 중"을 쓰면 진행 중인 칸과 **같은 말**이 되어, 목록만 봐서는 문서가
  // 멈춰 있다는 것을 알 수 없다(S39). 여기가 그 구별이 사용자에게 처음 보이는 자리다.
  if (node.status === 'parked') return '배정 대기';
  if (node.status === 'current') return '대기 중';
  return '-';
}

export interface ApprovalFlowStepperProps {
  nodes: ApprovalFlowNode[];
}

export function ApprovalFlowStepper({ nodes }: ApprovalFlowStepperProps) {
  if (nodes.length === 0) return null;

  return (
    // 목록(ol/li)이 아니라 라벨 붙은 그룹으로 둔다 — 단계와 커넥터를 한 flex 흐름에 놓아야
    // 폭이 균등해지는데, li로 감싸려면 `display:contents`가 필요하고 그건 브라우저에 따라
    // 목록 시맨틱 자체를 지우는 부작용이 있다. 진행 정보는 각 단계의 텍스트로 이미 읽힌다.
    <Card
      surface="soft"
      elevation="none"
      padding="none"
      role="group"
      aria-label="결재 진행 단계"
      className="flex items-start overflow-x-auto px-4 pb-2.5 pt-3"
    >
      {nodes.map((node, index) => (
        <Fragment key={node.key}>
          <div className="flex w-[88px] flex-none flex-col items-center text-center">
            <div
              className={`relative grid h-[30px] w-[30px] flex-none place-items-center rounded-full border-2 ${NODE_CLASS[node.status]}`}
            >
              <StepIcon status={node.status} />
              {node.status === 'current' && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-[3px] rounded-full border-2 border-dashed border-amber-600 opacity-70 motion-safe:animate-step-spin"
                />
              )}
            </div>
            {/* max-w-full이 없으면 truncate가 걸리지 않는다 — flex 항목의 폭이 내용으로 늘어나
                말줄임 대신 옆 단계를 밀어낸다. */}
            <span
              className={`mt-2 flex max-w-full items-center gap-0.5 truncate text-[11.5px] font-bold tracking-tight ${LABEL_CLASS[node.status]}`}
            >
              {/* 결재선 정의에 없는 단계 = 데이터 불일치. 조용히 메우면 결재라인 소실 같은
                  손상이 화면에 정상처럼 보여 오래 가려진다(2026-07-24 사고가 두 달 늦게 발견됐다). */}
              {node.isOrphan && (
                <AlertTriangleIcon
                  className="h-3 w-3 flex-none text-amber-500"
                  aria-hidden="true"
                />
              )}
              <span className="truncate">{node.label}</span>
            </span>
            <span className={`mt-0.5 max-w-full truncate text-[12.5px] font-semibold ${NAME_CLASS[node.status]}`}>
              {node.name}
            </span>
            <span className="mt-px text-[10.5px] font-medium tabular-nums text-ink-400">
              {stepTimeText(node)}
            </span>
            <span className="sr-only">
              {STATUS_TEXT[node.status]}
              {node.isOrphan ? ' · 결재선 정의에 없는 단계입니다 (데이터 확인 필요)' : ''}
            </span>
          </div>

          {index < nodes.length - 1 && (
            // 커넥터 중심을 30px 노드 중심(=15px)에 맞춘다. max-w가 피치 고정의 핵심 —
            // 넓은 화면에서 전부 상한에 걸려야 카드 간 단계 x가 같아진다(위 doc 주석).
            <div aria-hidden="true" className="h-[30px] min-w-7 max-w-[140px] flex-1 pt-3.5">
              {node.status === 'done' ? (
                <span className="block h-0.5 w-full rounded-full bg-emerald-500" />
              ) : (
                <span className="block w-full border-t-2 border-dashed border-ink-300" />
              )}
            </div>
          )}
        </Fragment>
      ))}
    </Card>
  );
}
